import React from "react";
import { classNames } from "../../../helpers";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForUsers } from "../../../utils/confighelpers";
import AddLocationPopup from "./AddLocationPopup";
import { useToast } from "../../../provider/ToastProvider";
import { FcModal, FieldError, FieldLabel, Icon, inputClass } from "./ui";

/** Club locations for the schedule pop-ups/drawers: pick one, or add a new one (AddLocationPopup). */

export const locationLine = (loc) => [loc?.houseNumber && loc?.address ? `${loc.address} ${loc.houseNumber}` : loc?.address, loc?.city].filter(Boolean).join(", ");

/** Selectable list of the club's locations (+ add a new one). value = location _id. */
export function LocationPicker({ value, onChange, error }) {
  const [locations, setLocations] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [addOpen, setAddOpen] = React.useState(false);
  const [renaming, setRenaming] = React.useState(null); // location being renamed
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance({ method: "get", url: `${getUrlForUsers()}listOfLocation` });
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setLocations(list);
      return list;
    } catch (e) {
      setLocations([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);
  React.useEffect(() => {
    load();
  }, [load]);
  React.useEffect(() => {
    if (!value && locations.length === 1) onChange(locations[0]._id, locations[0]); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);
  return (
    <div>
      {loading ? (
        <div className="h-16 animate-pulse rounded-2xl bg-gray-100" />
      ) : (
        <div className="flex flex-col gap-2" role="radiogroup" aria-label="Location">
          {locations.map((loc) => {
            const active = String(value) === String(loc._id);
            return (
              <div key={loc._id} className="relative">
              <button
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => onChange(loc._id, loc)}
                className={classNames(
                  "flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition-colors",
                  active ? "border-indigo-bz bg-[#FBF8FE] ring-2 ring-[#F3E9FF]" : error ? "border-red-300 bg-white" : "border-gray-200 bg-white hover:bg-gray-50"
                )}
              >
                <span className={classNames("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", active ? "bg-indigo-bz text-white" : "bg-[#FFE8EB] text-portland-orange")}>
                  <Icon className="h-5 w-5" d={<><path d="M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></>} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-extrabold text-gray-900">{loc.addressName || loc.address || "Location"}</span>
                  <span className="block truncate text-xs text-gray-500">{locationLine(loc)}</span>
                </span>
                <span className="w-9 shrink-0" aria-hidden="true" />
                <span className={classNames("flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2", active ? "border-indigo-bz" : "border-gray-300")}>
                  {active && <span className="h-2.5 w-2.5 rounded-full bg-indigo-bz" />}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setRenaming(loc)}
                aria-label={`Rename ${loc.addressName || "location"}`}
                title="Rename location"
                className="absolute right-10 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-xl text-gray-400 hover:bg-[#F3E9FF] hover:text-indigo-bz"
              >
                <Icon className="h-4 w-4" d={<><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>} />
              </button>
              </div>
            );
          })}
          {locations.length === 0 && <p className="text-sm text-gray-600">You haven't added a location yet.</p>}
          <button type="button" onClick={() => setAddOpen(true)} className="mt-1 self-start text-sm font-extrabold text-indigo-bz hover:text-indigo-bz-hover">
            + Add a new location
          </button>
        </div>
      )}
      <FieldError>{error}</FieldError>
      <AddLocationPopup
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={async (body) => {
          const list = await load();
          // select the location just added (newest one with that name + address)
          const added = [...list].reverse().find((l) => l.addressName === body.addressName && l.address === body.address);
          if (added) onChange(added._id, added);
        }}
      />
      <RenameLocationPopup
        location={renaming}
        onClose={() => setRenaming(null)}
        onRenamed={async (id) => {
          const list = await load();
          // keep the selection, with the new name
          if (String(value) === String(id)) {
            const updated = list.find((l) => String(l._id) === String(id));
            if (updated) onChange(updated._id, updated);
          }
        }}
      />
    </div>
  );
}

export const locationPayload = (loc) => ({
  locationId: loc?._id,
  address: loc?.address,
  addressName: loc?.addressName,
  houseNumber: loc?.houseNumber,
  city: loc?.city,
  zip: loc?.zip ? loc.zip : "",
});

/**
 * Rename a club location (replaces the old My Locations page for clubs).
 * PUT users/updateLocation, same body as the old myLocation/AddEditLocationModal edit.
 * The backend also updates the name on every session at this location.
 */
function RenameLocationPopup({ location, onClose, onRenamed = () => null }) {
  const { addSuccessToast } = useToast();
  const [name, setName] = React.useState("");
  const [error, setError] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const open = !!location;

  React.useEffect(() => {
    if (location) {
      setName(location.addressName || "");
      setError("");
      setSaving(false);
    }
  }, [location]);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Give this location a name members will recognise.");
      return;
    }
    if (trimmed === (location.addressName || "")) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const res = await axiosInstance({ method: "put", url: `${getUrlForUsers()}updateLocation`, data: { ...location, locationId: location._id, addressName: trimmed } });
      if (res?.data?.statusCode === 200) {
        addSuccessToast("Location renamed");
        onRenamed(location._id);
        onClose();
      } else {
        setError(typeof res?.data?.message === "string" && res.data.message ? res.data.message : "Couldn't rename this location.");
      }
    } catch (e) {
      setError("Couldn't reach the server. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FcModal
      open={open}
      onClose={onClose}
      eyebrow="LOCATION"
      title="Rename location"
      subtitle="The new name shows on all sessions at this location, including ones members already booked."
      primaryLabel="Save name"
      onPrimary={save}
      busy={saving}
    >
      {location && (
        <div className="flex items-center gap-3 rounded-2xl bg-[#F7F4F8] p-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FFE8EB] text-portland-orange">
            <Icon className="h-5 w-5" d={<><path d="M12 21s-7-6.2-7-11a7 7 0 1 1 14 0c0 4.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></>} />
          </span>
          <span className="min-w-0 text-sm text-gray-700">{locationLine(location) || "Address not set"}</span>
        </div>
      )}
      <div>
        <FieldLabel htmlFor="fc-loc-rename">Location name</FieldLabel>
        <input
          id="fc-loc-rename"
          type="text"
          value={name}
          maxLength={60}
          autoFocus
          onChange={(e) => {
            setName(e.target.value);
            setError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              save();
            }
          }}
          className={inputClass(error)}
        />
        <FieldError>{error}</FieldError>
      </div>
    </FcModal>
  );
}
